import type { StateCreator } from 'zustand';
import { DocumentUrlsModel } from 'shared/domain/document/documentUrlsModel';
import { setLimitedMapEntry } from 'shared/utils/limitedCache';
import { UrlCacheSlice, UrlCacheSliceState, LocalUrlFetchResponse } from './types';
import type { DocumentationStoreSlices } from '../types';

export * from './types';

const MAX_URL_REQUESTS_CACHE_ENTRIES = 500;
const MAX_URL_RESOLVED_CACHE_ENTRIES = 500;

const INITIAL_URL_CACHE_STATE: UrlCacheSliceState = {
  localUrlRequests: new Map(),
  savedUrlRequests: new Map(),
  resolvedSavedUrls: new Map(),
};

type UrlCacheSliceCreator = StateCreator<DocumentationStoreSlices, [], [], UrlCacheSlice>;

export const createUrlCacheSlice: UrlCacheSliceCreator = (set, get) => ({
  ...INITIAL_URL_CACHE_STATE,

  getLocalUrlRequest: (url: string): Promise<LocalUrlFetchResponse> | undefined => {
    return get().localUrlRequests.get(url);
  },

  setLocalUrlRequest: (url: string, request: Promise<LocalUrlFetchResponse>): void => {
    const { localUrlRequests } = get();
    const newMap = new Map(localUrlRequests);
    setLimitedMapEntry(newMap, url, request, MAX_URL_REQUESTS_CACHE_ENTRIES);
    set({ localUrlRequests: newMap });
  },

  getSavedUrlRequest: (url: string): Promise<DocumentUrlsModel> | undefined => {
    return get().savedUrlRequests.get(url);
  },

  setSavedUrlRequest: (url: string, request: Promise<DocumentUrlsModel>): void => {
    const { savedUrlRequests } = get();
    const newMap = new Map(savedUrlRequests);
    setLimitedMapEntry(newMap, url, request, MAX_URL_REQUESTS_CACHE_ENTRIES);
    set({ savedUrlRequests: newMap });
  },

  getResolvedSavedUrl: (fileId: string): DocumentUrlsModel | undefined => {
    return get().resolvedSavedUrls.get(fileId);
  },

  setResolvedSavedUrl: (fileId: string, urls: DocumentUrlsModel): void => {
    const { resolvedSavedUrls } = get();
    const newMap = new Map(resolvedSavedUrls);
    setLimitedMapEntry(newMap, fileId, urls, MAX_URL_RESOLVED_CACHE_ENTRIES);
    set({ resolvedSavedUrls: newMap });
  },

  clearUrlCaches: (): void => {
    set({
      localUrlRequests: new Map(),
      savedUrlRequests: new Map(),
      resolvedSavedUrls: new Map(),
    });
  },
});
